import { WritersRoomPhase } from '@/hooks/useWritersRoom';
import { ScenePack } from '@/lib/types';
import { Close } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

interface WritersRoomValidationIssuesProps {
  phase: WritersRoomPhase;
  validation: ScenePack['validation'];
  onRemoveIssue: (category: 'continuity' | 'lore', issueId: string) => void;
}

export function WritersRoomValidationIssues({
  phase,
  validation,
  onRemoveIssue,
}: WritersRoomValidationIssuesProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (phase !== 'needs_fixes' || !validation) return null;

  const continuityIssues = validation.continuity?.issues ?? [];
  const loreIssues = validation.lore?.issues ?? [];

  if (continuityIssues.length === 0 && loreIssues.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Divider sx={{ my: 1 }} />
      <Typography
        variant="caption"
        color="warning.main"
        sx={{ fontWeight: 600, textTransform: 'uppercase', display: 'block' }}
      >
        {t('writers_room.validation_issues')}
      </Typography>

      <Stack spacing={1.5}>
        {continuityIssues.map(issue => (
          <IssueCard
            key={issue.id}
            issueId={issue.id}
            message={issue.message}
            categoryLabel={t('writers_room.validation_categories.continuity')}
            onRemove={() => onRemoveIssue('continuity', issue.id)}
            borderColor={alpha(theme.palette.warning.main, 0.3)}
          />
        ))}

        {loreIssues.map(issue => (
          <IssueCard
            key={issue.id}
            issueId={issue.id}
            message={issue.message}
            categoryLabel={t('writers_room.validation_categories.lore')}
            onRemove={() => onRemoveIssue('lore', issue.id)}
            borderColor={alpha(theme.palette.warning.main, 0.3)}
          />
        ))}
      </Stack>
    </Box>
  );
}

interface IssueCardProps {
  issueId: string;
  message: string;
  categoryLabel: string;
  onRemove: () => void;
  borderColor: string;
}

function IssueCard({ message, categoryLabel, onRemove, borderColor }: IssueCardProps) {
  return (
    <Card variant="outlined" sx={{ position: 'relative', borderColor }}>
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ position: 'absolute', top: 4, right: 4 }}
      >
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
      <CardContent sx={{ p: '12px !important' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}
        >
          {categoryLabel}
        </Typography>
        <Typography variant="body2">{message}</Typography>
      </CardContent>
    </Card>
  );
}
